import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buttonVariants } from './button-variants';

/**
 * Contract: docs/FRONTEND_COMPONENT_INVENTORY.md §5 (IconButton).
 *
 * An icon-only `button`. The glyph says nothing to a screen reader and little to
 * someone guessing, so `label` is REQUIRED and becomes both the `aria-label` and
 * the `title` (the tooltip for a pointer user).
 *
 * Hit area, spec R4 (C-4):
 * - `size-icon-lg` (32px) with a `min-h-6 min-w-6` (24px) floor: WCAG 2.2 2.5.8,
 *   on every pointer and even if a caller overrides the size.
 * - `pointer-coarse:min-h-touch pointer-coarse:min-w-touch`: the 48px
 *   `touch-target-min` token when the primary pointer is a finger. A fine pointer
 *   keeps the compact 32px, so the desktop layout does not change.
 *
 * The colour, border and focus classes come from `buttonVariants`, not from a
 * second copy of them.
 */
export interface IconButtonProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'title' | 'children'
> {
  /** What the button does, in Spanish. It is the accessible name AND the tooltip. */
  label: string;
  icon: LucideIcon;
  variant?: 'ghost' | 'outline';
  ref?: React.Ref<HTMLButtonElement>;
}

export function IconButton({
  label,
  icon: Icon,
  variant = 'ghost',
  type = 'button',
  className,
  ref,
  ...props
}: IconButtonProps): React.JSX.Element {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        buttonVariants({ variant, size: null }),
        'size-icon-lg min-h-6 min-w-6 shrink-0 text-fg-muted pointer-coarse:min-h-touch pointer-coarse:min-w-touch',
        className,
      )}
      {...props}
    >
      <Icon aria-hidden="true" className="size-icon-sm" />
    </button>
  );
}
